import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Stack,
  Typography,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Close as CloseIcon, Sync as SyncIcon } from '@mui/icons-material';
import { ReactComponent as ToolCropIcon } from 'new/components/icons/tool.svg';
import { formatRelativeTime, formatDateTime } from '../../utils/dateFormatters';
import { getStatusLabel, getToolKindLabel, getToolDisplayName } from '../../utils/toolLabels';
import { useGetToolQuery, useSyncToolMutation } from '../../state/aiApi';
import { useSnackbar } from 'new/contexts/SnackbarContext';
import { extractApiError } from '../../utils/errorUtils';
import { getStatusChipColor } from 'new/modules/ai/utils/statusColorHelpers';
import styles from './ToolDetailsDrawer.module.scss';

interface ToolDetailsDrawerProps {
  toolId: number | null;
  open: boolean;
  onClose: () => void;
}

export const ToolDetailsDrawer: React.FC<ToolDetailsDrawerProps> = ({ toolId, open, onClose }) => {
  const { data: tool, isLoading } = useGetToolQuery(toolId!, {
    skip: !toolId || !open,
  });
  const [syncTool, { isLoading: isSyncing }] = useSyncToolMutation();
  const { showSuccess, showError } = useSnackbar();

  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when collapsing so text always shows from the top.
  useEffect(() => {
    if (!descriptionExpanded && descriptionRef.current) {
      descriptionRef.current.scrollTop = 0;
    }
  }, [descriptionExpanded]);

  useEffect(() => {
    if (!open) {
      setDescriptionExpanded(false);
      setIsDescriptionOverflowing(false);
    }
  }, [open]);

  useEffect(() => {
    if (descriptionExpanded) return;
    const id = requestAnimationFrame(() => {
      const el = descriptionRef.current;
      if (el) {
        setIsDescriptionOverflowing(el.scrollHeight > el.clientHeight);
      } else {
        setIsDescriptionOverflowing(false);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [tool?.description, open, descriptionExpanded]);

  const handleSync = async () => {
    if (!toolId || !tool?.data_set_id) return;
    try {
      const result = await syncTool({ toolId, nexset_id: tool.data_set_id }).unwrap();
      if (result.created_new_version) {
        showSuccess(`Tool synced — updated to v${result.version}`);
      } else {
        showSuccess('Tool synced — already up to date');
      }
    } catch (err) {
      showError(extractApiError(err, 'Failed to sync tool'));
    }
  };

  if (!toolId) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 480, maxWidth: '100%' },
      }}
    >
      <div className={styles.drawerContent}>
        {/* Header */}
        <div className={styles.header}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" alignItems="flex-start" spacing={1.5} flex={1}>
              <div className={styles.toolIcon}>
                <ToolCropIcon />
              </div>
              <div className={styles.headerContent}>
                <Typography variant="h6" className={styles.headerTitle}>
                  {tool ? getToolDisplayName(tool) : 'Loading...'}
                </Typography>
              </div>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {tool?.kind === 'nexset_read' && (
                <Tooltip title="Sync from Nexset">
                  <span>
                    <IconButton onClick={handleSync} size="small" disabled={isSyncing}>
                      {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          {tool?.description && (
            <Box className={styles.descriptionContainer}>
              <Box
                ref={descriptionRef}
                className={
                  descriptionExpanded ? styles.descriptionScrollBox : styles.descriptionClipBox
                }
              >
                <Typography variant="body2" color="text.secondary">
                  {tool.description}
                </Typography>
              </Box>
              {isDescriptionOverflowing && (
                <Typography
                  variant="caption"
                  color="primary"
                  component="button"
                  aria-expanded={descriptionExpanded}
                  className={styles.descriptionToggle}
                  onClick={() => setDescriptionExpanded((prev) => !prev)}
                >
                  {descriptionExpanded ? 'Show less' : 'Show more'}
                </Typography>
              )}
            </Box>
          )}

          {tool?.status && (
            <Box className={styles.statusBadge}>
              <ChipBadge
                label={getStatusLabel(tool.status)}
                color={getStatusChipColor(tool.status)}
              />
            </Box>
          )}
        </div>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && tool && (
          <>
            {/* Quick Stats */}
            <div className={styles.statsSection}>
              <div className={styles.statItem}>
                <Typography variant="caption" color="text.secondary">
                  KIND
                </Typography>
                <Typography variant="body2">{getToolKindLabel(tool.kind)}</Typography>
              </div>
              <div className={styles.statItem}>
                <Typography variant="caption" color="text.secondary">
                  CREATED
                </Typography>
                <Typography variant="body2">{formatRelativeTime(tool.created_at)}</Typography>
              </div>
              <div className={styles.statItem}>
                <Typography variant="caption" color="text.secondary">
                  UPDATED
                </Typography>
                <Typography variant="body2">{formatRelativeTime(tool.updated_at)}</Typography>
              </div>
            </div>

            <Divider />

            {/* Technical Details */}
            <div className={styles.section}>
              <Typography variant="subtitle2" className={styles.sectionTitle} sx={{ mb: 2 }}>
                Technical Details
              </Typography>
              <Stack spacing={1.5}>
                <div className={styles.techItem}>
                  <Typography variant="caption" color="text.secondary">
                    Tool ID
                  </Typography>
                  <Typography variant="body2" className={styles.techValue}>
                    {tool.id}
                  </Typography>
                </div>
                {tool.key && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Tool Key
                    </Typography>
                    <Typography variant="body2" className={styles.techValue}>
                      {tool.key}
                    </Typography>
                  </div>
                )}
                {tool.data_set_id && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Nexset ID
                    </Typography>
                    <Typography variant="body2" className={styles.techValue}>
                      {tool.data_set_id}
                    </Typography>
                  </div>
                )}
                {tool.access_mode === 'marketplace' && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Source
                    </Typography>
                    <Typography variant="body2">
                      Marketplace Data Product — reads with your Nexla access, no credential
                      required
                    </Typography>
                  </div>
                )}
                {tool.semver && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Version
                    </Typography>
                    <Typography variant="body2">{tool.semver}</Typography>
                  </div>
                )}
                {tool.created_at && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2">{formatDateTime(tool.created_at)}</Typography>
                  </div>
                )}
                {tool.updated_at && (
                  <div className={styles.techItem}>
                    <Typography variant="caption" color="text.secondary">
                      Updated At
                    </Typography>
                    <Typography variant="body2">{formatDateTime(tool.updated_at)}</Typography>
                  </div>
                )}
              </Stack>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};
